import { useState, useEffect } from "react";
import { v4 as uuidv4 } from 'uuid';

import apiClient from "../../Api/apiConfig";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Edit, Trash2, Building, XCircle } from "lucide-react";

const columnHelper = createColumnHelper();

export default function AgencesTable() {
  const [data, setData] = useState([]);
  const [landlords, setLandlords] = useState([]); // Liste des propriétaires
  const [error, setError] = useState(null);
  const [currentAgence, setCurrentAgence] = useState({
    id: "",
    landlord_id: "",
    name: "",
    address: "",
    phone: "",
    email: "",
  });
  const [modalType, setModalType] = useState(null);
  const [selectedAgence, setSelectedAgence] = useState(null);

  // Récupération des agences et des propriétaires
  useEffect(() => {
    const fetchAgences = async () => {
      try {
        const response = await apiClient.get("/agencies");
        setData(response.data);
      } catch (err) {
        console.error("Erreur lors du chargement des agences:", err.message);
        setError("Erreur lors du chargement des agences");
      }
    };

    const fetchLandlords = async () => {
      try {
        const response = await apiClient.get("/landlords");
        setLandlords(response.data);
      } catch (err) {
        console.error("Erreur lors du chargement des propriétaires:", err.message);
      }
    };

    fetchAgences();
    fetchLandlords();
  }, []);

  // Ouvrir les modals
  const openAddModal = () => {
    // Génération automatique d'un ID pour l'agence
    setCurrentAgence({
      id: uuidv4(),
      landlord_id: "",
      name: "",
      address: "",
      phone: "",
      email: "",
    });
    setModalType("add");
  };

  const openEditModal = (agence) => {
    setCurrentAgence(agence);
    setModalType("edit");
  };

  const openDeleteModal = (agence) => {
    setSelectedAgence(agence);
    setModalType("delete");
  };

  // Ajouter une agence
  const addAgence = async () => {
    if (!currentAgence.id || !currentAgence.landlord_id || !currentAgence.name) {
      setError("ID, le propriétaire et le nom sont requis.");
      return;
    }
    try {
      const response = await apiClient.post("/agencies", currentAgence);
      setData([...data, response.data]);
      setModalType(null);
    } catch (err) {
      console.error("Erreur lors de l'ajout:", err.message);
      setError("Erreur lors de l'ajout de l'agence");
    }
  };

  // Modifier une agence
  const updateAgence = async () => {
    if (!currentAgence.landlord_id || !currentAgence.name) {
      setError("Le propriétaire et le nom sont requis.");
      return;
    }
    try {
      await apiClient.put(`/agencies/${currentAgence.id}`, currentAgence);
      setData(data.map(item => (item.id === currentAgence.id ? currentAgence : item)));
      setModalType(null);
    } catch (err) {
      console.error("Erreur lors de la modification:", err.message);
      setError("Erreur lors de la modification de l'agence");
    }
  };

  // Supprimer une agence
  const deleteAgence = async () => {
    try {
      await apiClient.delete(`/agencies/${selectedAgence.id}`);
      setData(data.filter(item => item.id !== selectedAgence.id));
      setModalType(null);
    } catch (err) {
      console.error("Erreur lors de la suppression:", err.message);
      setError("Erreur lors de la suppression de l'agence");
    }
  };

  const columns = [
    columnHelper.accessor("name", { header: "Nom", cell: (info) => info.getValue() }),
    columnHelper.accessor("email", { header: "Email", cell: (info) => info.getValue() }),
    columnHelper.accessor("phone", { header: "Téléphone", cell: (info) => info.getValue() }),
    columnHelper.accessor("address", { header: "Adresse", cell: (info) => info.getValue() }),
    columnHelper.accessor("landlord_id", { 
      header: "Propriétaire", 
      cell: (info) =>
        landlords.find(l => l.id === info.getValue())?.name || "Inconnu"
    }),
    columnHelper.accessor("id", {
      header: "Actions",
      cell: (info) => (
        <div className="flex space-x-2">
          <button onClick={() => openEditModal(info.row.original)} className="text-blue-600 hover:text-blue-800">
            <Edit className="h-5 w-5" />
          </button>
          <button onClick={() => openDeleteModal(info.row.original)} className="text-red-600 hover:text-red-800">
            <Trash2 className="h-5 w-5" />
          </button>
        </div>
      ),
    }),
  ];

  const table = useReactTable({ data, columns, getCoreRowModel: getCoreRowModel() });

  return (
    <div className="mt-8 flex flex-col bg-white shadow rounded-lg p-4 relative">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-medium">Gestion des agences</h2>
        <button onClick={openAddModal} className="px-4 py-2 bg-blue-600 text-white rounded">
          <Building className="h-5 w-5 mr-2 inline" /> Ajouter une agence
        </button>
      </div>

      {error && <div className="mb-4 text-red-600">{error}</div>}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-gray-50">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} className="px-3 py-3.5 text-left text-sm font-semibold">
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="px-3 py-4 text-sm">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal centré et en premier plan */}
      {modalType && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-80 z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-1/3 relative">
            <button onClick={() => setModalType(null)} className="absolute top-2 right-2 text-gray-600 hover:text-gray-900">
              <XCircle className="w-6 h-6" />
            </button>
            {modalType === "delete" ? (
              <>
                <h3 className="text-lg font-semibold">Confirmer la suppression</h3>
                <p className="my-4">
                  Voulez-vous vraiment supprimer l'agence "{selectedAgence?.name}" ?
                </p>
                <div className="flex justify-end">
                  <button
                    onClick={() => setModalType(null)}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded mr-2"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={deleteAgence}
                    className="px-4 py-2 bg-red-600 text-white rounded"
                  >
                    Supprimer
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 className="text-lg font-semibold">
                  {modalType === "add" ? "Ajouter" : "Modifier"} une agence
                </h3>
                {/* En mode ajout, l'ID est généré automatiquement et ne s'affiche pas à modifier */}
                {modalType === "edit" && (
                  <input
                    type="text"
                    placeholder="ID"
                    value={currentAgence.id}
                    disabled
                    className="w-full p-2 border rounded my-2 bg-gray-200"
                  />
                )}
                <select
                  value={currentAgence.landlord_id}
                  onChange={(e) =>
                    setCurrentAgence({
                      ...currentAgence,
                      landlord_id: e.target.value,
                    })
                  }
                  className="w-full p-2 border rounded my-2"
                >
                  <option value="">Sélectionnez un propriétaire</option>
                  {landlords.map((landlord) => (
                    <option key={landlord.id} value={landlord.id}>
                      {landlord.name}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="Nom"
                  value={currentAgence.name}
                  onChange={(e) =>
                    setCurrentAgence({ ...currentAgence, name: e.target.value })
                  }
                  className="w-full p-2 border rounded my-2"
                />
                <input
                  type="text"
                  placeholder="Adresse"
                  value={currentAgence.address}
                  onChange={(e) =>
                    setCurrentAgence({ ...currentAgence, address: e.target.value })
                  }
                  className="w-full p-2 border rounded my-2"
                />
                <input
                  type="text"
                  placeholder="Téléphone"
                  value={currentAgence.phone}
                  onChange={(e) =>
                    setCurrentAgence({ ...currentAgence, phone: e.target.value })
                  }
                  className="w-full p-2 border rounded my-2"
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={currentAgence.email}
                  onChange={(e) =>
                    setCurrentAgence({ ...currentAgence, email: e.target.value })
                  }
                  className="w-full p-2 border rounded my-2"
                />
                <button
                  onClick={modalType === "add" ? addAgence : updateAgence}
                  className="w-full bg-blue-600 text-white py-2 rounded"
                >
                  {modalType === "add" ? "Ajouter" : "Modifier"}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
